// software license for '@ua-parser-js/pro-business' was purchased by Marc
import { UAParser as userAgentParser } from '@ua-parser-js/pro-business'
import { MOBILE_BREAKPOINT } from '../components/styles/breakpoints'
import { webKitViewTransitionBugs } from './webKitViewTransitionBugs'

export const isBrowser = () => typeof window !== 'undefined'
export const isAndroid =
    typeof window !== 'undefined' && /Android/.test(window.navigator.userAgent)

/**
 * Return true/false if the user is on iOS
 */
export const isIOS = (userAgent?: string) =>
    userAgentParser(userAgent)?.os?.name === 'iOS'

/**
 * Detects Safari v26 and newer
 */
export const isLatestSafari = (userAgent: string) => {
    return parseFloat(userAgentParser(userAgent).browser.version) >= 26
}

/**
 * Apple decided older iPadOS return as "MacIntel"
 * so we diff that + "iPad" and touchpoints
 */
export const isIPadOS = (platform: string, maxTouchPoints?: number) => {
    if (!maxTouchPoints) return false
    return (
        (/MacIntel/.test(platform) || /iPad/.test(platform)) &&
        maxTouchPoints > 2
    )
}

export const isIpad =
    typeof window !== 'undefined' &&
    isIPadOS(window.navigator.platform, window.navigator.maxTouchPoints)

/**
 * iOS 13.6+ support for detecting iOS & iPad
 */
export const isIPhoneOrIPad = (platform: string, touchpoints?: number) =>
    platform === 'iPhone' || isIPadOS(platform, touchpoints)

export const isIDevice =
    typeof window !== 'undefined' &&
    isIPhoneOrIPad(window.navigator.platform, window.navigator.maxTouchPoints)

export const isMobileSafari26 =
    typeof window !== 'undefined' &&
    isIDevice &&
    isLatestSafari(window.navigator.userAgent)

/**
 * Every iOS browser and Safari run WebKit, and WebKit ships with the OS, so engine
 * bugs are keyed on the iOS version there and on the Safari version on macOS.
 * A standalone (PWA) UA has no `Version/` token, which the OS version covers on iOS.
 */
const isWebKit =
    typeof window !== 'undefined' && /Apple/.test(window.navigator.vendor)

const webKitVersion = (() => {
    if (!isWebKit) return NaN
    const { browser, os } = userAgentParser(window.navigator.userAgent)
    return parseFloat(os?.name === 'iOS' ? os.version : browser.version)
})()

// bug links and version ranges live in webKitViewTransitionBugs.ts
const vtBugs = webKitViewTransitionBugs(webKitVersion, isIDevice)
export const WEBKIT_VT_ROOT_CHROME_FLASH = isWebKit && vtBugs.rootChromeFlash
export const WEBKIT_VT_VIDEO_SNAPSHOT_BLANK =
    isWebKit && vtBugs.videoSnapshotBlank
export const WEBKIT_VT_BACKDROP_FILTER_DROPPED =
    isWebKit && vtBugs.backdropFilterDropped
export const WEBKIT_VT_FIXED_CLIPPING = isWebKit && vtBugs.fixedClipping
export const WEBKIT_VT_CSS_RULE_SNAP = isWebKit && vtBugs.cssRuleSnap

export const isIOSPWA =
    typeof window !== 'undefined' && window.navigator['standalone'] === true

// iOS 27 PWAs blur the status bar unless a viewport-wide fixed container sits at the top edge
// (WebKit LocalFrameView::fixedContainerEdges); Layout renders one under this flag
export const IOS_PWA_STATUS_BAR_BLUR =
    isWebKit && isIDevice && isIOSPWA && webKitVersion >= 27

export const isChromePWA =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches

export const isPWA = isIOSPWA || isChromePWA

export const isMobile = () =>
    typeof window !== 'undefined' &&
    (window.innerWidth <= MOBILE_BREAKPOINT ||
        window.innerHeight <= MOBILE_BREAKPOINT)

export const isScreenOrientationSupported =
    typeof window !== 'undefined' &&
    typeof window.screen.orientation === 'object' &&
    (isAndroid || isIDevice)

/**
 * Primary input mechanism cannot hover at all or cannot conveniently hover
 * (e.g., many mobile devices emulate hovering when the user performs an
 * inconvenient long tap), or there is no primary pointing input mechanism
 */
export const canNotHover =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none)').matches

export const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window ||
        window.navigator.maxTouchPoints > 0 ||
        canNotHover ||
        'ontouchstart' in document.documentElement ||
        window.navigator['msMaxTouchPoints'] > 0 ||
        window.navigator['msPointerEnabled'] === true)

export const isDarkModePreferred =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches

export const isReducedMotionPreferred =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
